import type { ChannelType } from "@workspace/api-client-react";
import { DEVICE_CMD } from "./device-commands.ts";
import type { QueueSnapshotItem } from "./playback-engine.ts";

export interface CommandOutcome {
  ok: boolean;
  result?: Record<string, unknown>;
}

/**
 * What a playback target's `play` (or a transport routed through
 * `controlChannel`) may resolve with. For the bare `play` hook, returning void
 * (or a void promise) keeps the historical optimistic ack; resolving
 * `{ ok: false }` makes the relayed command-result honest — e.g.
 * `reason: "autoplay-blocked"` when the browser rejected `play()` on a kiosk
 * that never received a user gesture.
 *
 * `controlChannel` is stricter: it MUST resolve a PlayOutcome. A silent drop
 * (paused a channel that wasn't playing, played an empty playlist, …) reports
 * `{ ok: false, reason }` with a short machine reason key so the operator's
 * console can explain why nothing happened. Well-known keys (translated
 * console-side): "channelNotActive", "nothingScheduled", "noScreenMedia",
 * "takeoverActive", "liveAnnouncementActive", "emptyPlaylist", "historyEmpty",
 * "notHandled".
 */
export interface PlayOutcome {
  ok: boolean;
  reason?: string;
}

export interface RealtimeCommand {
  command: string;
  args?: Record<string, unknown>;
}

/** A transport action that can target a single channel via `controlChannel`. */
export type ChannelTransport =
  | { type: "play" }
  | { type: "pause" }
  | { type: "next" }
  | { type: "previous" }
  | { type: "seek"; positionSec: number }
  | { type: "setVolume"; volume: number }
  | { type: "setMute"; muted: boolean };

export interface MediaRefArgs {
  playlistId?: string;
  source?: string;
  id?: string;
}

export interface PlaybackCommandTarget {
  /** May resolve a PlayOutcome so the relayed ack reflects the real result. */
  play: () => void | Promise<PlayOutcome | void>;
  pause: () => void;
  stop: () => void;
  /** May return a PlayOutcome so a blocked skip acks honestly (see PlayOutcome). */
  next: () => void | PlayOutcome;
  /** May return a PlayOutcome (e.g. "historyEmpty" when nothing to go back to). */
  previous: () => void | PlayOutcome;
  setVolume: (v: number) => void;
  setMuted: (muted: boolean) => void;
  shuffleNow: () => void;
  setScheduleEnabled: (on: boolean) => void;
  /** May resolve a PlayOutcome (e.g. "emptyPlaylist") instead of a bare boolean. */
  playMediaRef: (ref: MediaRefArgs) => Promise<boolean | PlayOutcome>;
  addMediaRefToQueue: (ref: MediaRefArgs) => Promise<boolean>;
  removeFromQueue: (index: number) => void;
  reorderQueue: (fromIndex: number, toIndex: number) => void;
  getQueueSnapshot: () => {
    queue: QueueSnapshotItem[];
    current: QueueSnapshotItem | null;
  };
  resync: () => void;
  seek: (positionSec: number) => void;
  /**
   * MUST resolve a PlayOutcome for every action: silent drops report
   * `{ ok: false, reason }` so the console can explain why nothing happened.
   * A void resolution is treated as `{ ok: false, reason: "notHandled" }`.
   */
  controlChannel: (
    channelType: ChannelType,
    action: ChannelTransport,
  ) => PlayOutcome | Promise<PlayOutcome>;
}

export interface CommandProcessorHooks {
  forceSync: () => void;
  quit?: () => void;
  logout?: () => void;
  parseMediaRef: (args: Record<string, unknown>) => MediaRefArgs | null;
  onRestart?: () => void | Promise<void>;
  onClearCache?: () => void | Promise<void>;
}

const CHANNEL_TYPES: ReadonlySet<string> = new Set([
  "music",
  "ads",
  "announcements",
  "video",
]);

export function parseChannelType(v: unknown): ChannelType | null {
  return typeof v === "string" && CHANNEL_TYPES.has(v)
    ? (v as ChannelType)
    : null;
}

export function asBool(v: unknown, fallback = false): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return v === "true" || v === "1";
  if (typeof v === "number") return v !== 0;
  return fallback;
}

export function asNumber(v: unknown): number | null {
  const n = typeof v === "string" ? Number(v) : (v as number);
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

/**
 * Narrow a play/controlChannel resolution to a real PlayOutcome. Targets whose
 * handlers return incidental non-void values (arrow-body side effects) keep
 * the historical optimistic ack instead of tripping a false failure.
 */
function asPlayOutcome(v: unknown): PlayOutcome | null {
  return typeof v === "object" &&
    v !== null &&
    typeof (v as { ok?: unknown }).ok === "boolean"
    ? (v as PlayOutcome)
    : null;
}

async function routeChannelTransport(
  playback: PlaybackCommandTarget,
  channelType: ChannelType,
  command: string,
  args: Record<string, unknown>,
): Promise<CommandOutcome | null> {
  let action: ChannelTransport | null = null;
  switch (command) {
    case DEVICE_CMD.play:
      action = { type: "play" };
      break;
    case DEVICE_CMD.pause:
      action = { type: "pause" };
      break;
    case DEVICE_CMD.next:
      action = { type: "next" };
      break;
    case DEVICE_CMD.previous:
      action = { type: "previous" };
      break;
    case DEVICE_CMD.seek: {
      const pos = asNumber(args["positionSec"]);
      if (pos === null) return { ok: false, result: { error: "positionSec required" } };
      action = { type: "seek", positionSec: pos };
      break;
    }
    case DEVICE_CMD.setVolume: {
      const vol = asNumber(args["volume"]);
      if (vol === null) return { ok: false, result: { error: "volume required" } };
      action = { type: "setVolume", volume: vol };
      break;
    }
    case DEVICE_CMD.setMute: {
      const muted = asBool(args["muted"] ?? args["mute"], true);
      action = { type: "setMute", muted };
      break;
    }
    default:
      return null;
  }
  // Await the transport so a "play" the browser rejects (autoplay policy on a
  // gesture-less kiosk) acks honestly instead of reporting optimistic success.
  // Undefined is NOT success: a transport route that resolved nothing silently
  // dropped the command, and the operator must see that instead of ok:true.
  const outcome = asPlayOutcome(await playback.controlChannel(channelType, action));
  if (!outcome || !outcome.ok) {
    return {
      ok: false,
      result: {
        channelType,
        action: action.type,
        error: outcome?.reason ?? "notHandled",
      },
    };
  }
  return { ok: true, result: { channelType, action: action.type } };
}

/**
 * Apply a remote command to a playback engine and return a structured outcome
 * for relay as a command-result.
 */
export async function applyPlayerCommand(
  playback: PlaybackCommandTarget,
  cmd: RealtimeCommand,
  hooks: CommandProcessorHooks,
): Promise<CommandOutcome> {
  const args = cmd.args ?? {};

  const channelType = parseChannelType(args["channelType"]);
  if (channelType) {
    const routed = await routeChannelTransport(playback, channelType, cmd.command, args);
    if (routed) return routed;
  }

  switch (cmd.command) {
    case DEVICE_CMD.play: {
      // Await the real playback outcome: on autoplay-restricted kiosks play()
      // rejects, and the operator's console must see that failure, not an
      // optimistic ok relayed before the promise settled.
      const outcome = asPlayOutcome(await playback.play());
      if (outcome && !outcome.ok) {
        return { ok: false, result: { error: outcome.reason ?? "play failed" } };
      }
      return { ok: true };
    }
    case DEVICE_CMD.pause:
      playback.pause();
      return { ok: true };
    case DEVICE_CMD.stop:
      playback.stop();
      return { ok: true };
    case DEVICE_CMD.next: {
      // Thread a blocked skip out (e.g. "liveAnnouncementActive" while a live
      // broadcast holds the bus). Void keeps the historical optimistic ack.
      const outcome = asPlayOutcome(playback.next());
      if (outcome && !outcome.ok) {
        return { ok: false, result: { error: outcome.reason ?? "notHandled" } };
      }
      return { ok: true };
    }
    case DEVICE_CMD.previous: {
      const outcome = asPlayOutcome(playback.previous());
      if (outcome && !outcome.ok) {
        return { ok: false, result: { error: outcome.reason ?? "notHandled" } };
      }
      return { ok: true };
    }
    case DEVICE_CMD.setVolume: {
      const vol = asNumber(args["volume"]);
      if (vol === null) return { ok: false, result: { error: "volume required" } };
      playback.setVolume(vol);
      return { ok: true, result: { volume: vol } };
    }
    case DEVICE_CMD.setMute: {
      const m = asBool(args["muted"] ?? args["mute"], true);
      playback.setMuted(m);
      return { ok: true, result: { muted: m } };
    }
    case DEVICE_CMD.seek: {
      const pos = asNumber(args["positionSec"]);
      if (pos === null) return { ok: false, result: { error: "positionSec required" } };
      playback.seek(pos);
      return { ok: true, result: { positionSec: pos } };
    }
    case DEVICE_CMD.shuffle:
      playback.shuffleNow();
      return { ok: true };
    case DEVICE_CMD.playNow: {
      const ref = hooks.parseMediaRef(args);
      if (!ref) {
        return { ok: false, result: { error: "playlistId or source+id required" } };
      }
      const res = await playback.playMediaRef(ref);
      // Targets may resolve a PlayOutcome to explain the drop (e.g. the
      // playlist resolved but held zero playable items → "emptyPlaylist").
      const outcome = asPlayOutcome(res);
      if (outcome) {
        return outcome.ok
          ? { ok: true, result: { ...ref } }
          : {
              ok: false,
              result: { ...ref, error: outcome.reason ?? "notHandled" },
            };
      }
      return res === true
        ? { ok: true, result: { ...ref } }
        : { ok: false, result: { error: "could not resolve media" } };
    }
    case DEVICE_CMD.addToQueue: {
      const ref = hooks.parseMediaRef(args);
      if (!ref) {
        return { ok: false, result: { error: "playlistId or source+id required" } };
      }
      const ok = await playback.addMediaRefToQueue(ref);
      return ok
        ? { ok: true, result: { ...ref } }
        : { ok: false, result: { error: "could not queue media" } };
    }
    case DEVICE_CMD.removeFromQueue: {
      const index = asNumber(args["index"]);
      if (index === null) {
        return { ok: false, result: { error: "index required" } };
      }
      playback.removeFromQueue(index);
      return { ok: true, result: { index } };
    }
    case DEVICE_CMD.reorderQueue: {
      const fromIndex = asNumber(args["fromIndex"]);
      const toIndex = asNumber(args["toIndex"]);
      if (fromIndex === null || toIndex === null) {
        return { ok: false, result: { error: "fromIndex and toIndex required" } };
      }
      playback.reorderQueue(fromIndex, toIndex);
      return { ok: true, result: { fromIndex, toIndex } };
    }
    case DEVICE_CMD.getQueue: {
      const snapshot = playback.getQueueSnapshot();
      return { ok: true, result: snapshot as unknown as Record<string, unknown> };
    }
    case DEVICE_CMD.setSchedule: {
      const enabled = asBool(args["enabled"], true);
      playback.setScheduleEnabled(enabled);
      return { ok: true, result: { scheduleEnabled: enabled } };
    }
    case DEVICE_CMD.forceSync:
      hooks.forceSync();
      playback.resync();
      return { ok: true };
    case DEVICE_CMD.restart:
      await hooks.onRestart?.();
      return { ok: true };
    case DEVICE_CMD.clearCache:
      await hooks.onClearCache?.();
      return { ok: true };
    case DEVICE_CMD.quit:
      hooks.quit?.();
      return { ok: true };
    case DEVICE_CMD.logout:
      hooks.logout?.();
      return { ok: true };
    default:
      return { ok: false, result: { error: `unknown command: ${cmd.command}` } };
  }
}
